import { useState } from 'react';
import api from './api';
import { GUIDE_STEPS, PhotoAndPassportExamples } from './GuideContent';

const RED = 'var(--accent)';

const GENDERS      = ['Male', 'Female', 'Other'];
const MARITAL      = ['Single', 'Married', 'Divorced', 'Widowed'];
const DEPARTMENTS  = ['LX', 'AX', 'VX', 'RX', 'PX'];
const DEPT_FULL    = { LX: 'Lighting', AX: 'Audio', VX: 'Video', RX: 'Rigging', PX: 'Power' };
const DEPT_LEVELS  = ['Crew chief', 'Technician', 'Engineer', 'Operator'];
const LANG_LEVELS  = ['Native', 'Fluent', 'Basic', 'Enough for work'];
const CURRENCIES   = ['AED', 'EUR', 'USD', 'UAH', 'ZAR', 'SAR', 'KWD'];
const emptyBankAccount = () => ({ bankName: '', accountNumber: '', swift: '', accountHolderName: '', currency: 'EUR' });

const STEPS = ['Personal', 'Contact & address', 'Passport & ID', 'Financial', 'Travel & languages', 'Professional'];

const inputStyle = {
  width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: '6px', color: 'var(--text-primary)', padding: '10px 14px',
  fontSize: '14px', outline: 'none', boxSizing: 'border-box',
};
const inputDisabledStyle = { ...inputStyle, color: 'var(--text-dim)', cursor: 'not-allowed' };

function Field({ label, children, required, hint }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
        {label}{required && <span style={{ color: RED }}> *</span>}
      </label>
      {children}
      {hint && <p style={{ color: 'var(--text-dim)', fontSize: '11px', margin: '5px 0 0' }}>{hint}</p>}
    </div>
  );
}

function Row2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>{children}</div>;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocChip({ doc, onRemove }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 10px', marginTop: '6px' }}>
      <span style={{ fontSize: '13px' }}>📎</span>
      <span style={{ color: 'var(--text-body)', fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</span>
      <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{formatSize(doc.size)}</span>
      {onRemove && (
        <button type="button" onClick={onRemove} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '14px', padding: 0 }}>✕</button>
      )}
    </div>
  );
}

// Одна кнопка завантаження файлу(ів) з негайним аплоадом на сервер.
function UploadButton({ label, accept, multiple, uploading, onFiles }) {
  return (
    <label style={{
      display: 'inline-block', background: 'var(--bg-card)', border: '1px dashed var(--text-disabled)', color: 'var(--text-secondary)',
      borderRadius: '6px', padding: '9px 16px', fontSize: '13px', cursor: uploading ? 'not-allowed' : 'pointer',
      opacity: uploading ? 0.6 : 1,
    }}>
      {uploading ? 'Uploading…' : label}
      <input
        type="file" accept={accept} multiple={multiple} disabled={uploading}
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }}
      />
    </label>
  );
}

// ─── OnboardingPage ─────────────────────────────────────────────────────────────
// Багатокроковий візард заповнення профілю фрілансера. Показується після
// першого логіну, поки onboardingComplete === false. Файли/паспорти/мови
// зберігаються одразу через API; текстові поля — одним PATCH /me на кроці "Submit".
//
// mode='edit' — той самий компонент повторно використовується на сторінці
// Profile для перегляду/редагування вже заповненого профілю: кроки стають
// вільно клікабельними (без гейтингу), заголовок і кнопка підпису змінюються,
// з'являється "← Back".
export default function OnboardingPage({ freelancer, onComplete, mode = 'onboarding', onBack }) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Контекстна підказка — та сама інструкція, розбита по кроках (GuideContent.js),
  // яку фрілансер вже обов'язково прочитав на GuidePage перед онбордингом.
  const [showGuide, setShowGuide] = useState(false);

  const [form, setForm] = useState({
    firstName:  freelancer?.firstName  || '',
    middleName: freelancer?.middleName || '',
    lastName:   freelancer?.lastName   || '',
    dob:            freelancer?.dob            || '',
    nationality:    freelancer?.nationality     || '',
    gender:         freelancer?.gender          || '',
    maritalStatus:  freelancer?.maritalStatus   || '',
    birthCountry:   freelancer?.birthCountry    || '',
    birthCity:      freelancer?.birthCity       || '',
    phone:           freelancer?.phone           || '',
    whatsapp:        freelancer?.whatsapp        || '',
    additionalPhone: freelancer?.additionalPhone || '',
    passportNumber: freelancer?.passportNumber || '',
    passportExpiry: freelancer?.passportExpiry || '',
    address: {
      country: freelancer?.address?.country || '',
      city:    freelancer?.address?.city    || '',
      street:  freelancer?.address?.street  || '', // "Full address" в UI
      zip:     freelancer?.address?.zip     || '',
    },
    emergencyContact: {
      phone: freelancer?.emergencyContact?.phone || '',
      email: freelancer?.emergencyContact?.email || '',
    },
    travelPortDetail: {
      country: freelancer?.travelPortDetail?.country || '',
      city:    freelancer?.travelPortDetail?.city    || '',
      code:    freelancer?.travelPortDetail?.code    || '',
    },
    freelancerComment: freelancer?.freelancerComment || '',
  });
  const set     = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setNested = (group, k, v) => setForm(f => ({ ...f, [group]: { ...f[group], [k]: v } }));

  // ── Bank accounts (декілька банків, кожен зі своєю валютою) ───────────────
  const [bankAccounts, setBankAccounts] = useState(
    freelancer?.bankAccounts?.length ? freelancer.bankAccounts : [emptyBankAccount()]
  );
  const setBankField = (i, k, v) => setBankAccounts(list => list.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const addBankAccount = () => setBankAccounts(list => [...list, emptyBankAccount()]);
  const removeBankAccount = (i) => setBankAccounts(list => list.length > 1 ? list.filter((_, idx) => idx !== i) : list);

  // ── Additional passports (persisted immediately) ─────────────────────────
  const [passports, setPassports] = useState(freelancer?.additionalPassports || []);
  const [newPassport, setNewPassport] = useState({ number: '', expiry: '' });
  const [passportSaving, setPassportSaving] = useState(false);

  const addPassport = async () => {
    if (!newPassport.number) return;
    setPassportSaving(true);
    try {
      const res = await api.freelancers.addMyPassport(newPassport);
      setPassports(p => [...p, res.passport]);
      setNewPassport({ number: '', expiry: '' });
    } catch (e) { setError(e.message || 'Failed to add passport'); }
    finally { setPassportSaving(false); }
  };
  // Локальні незбережені правки вже доданих паспортів: { [passportId]: {number, expiry} }
  const [passportEdits, setPassportEdits] = useState({});
  const [passportRowSaving, setPassportRowSaving] = useState({});
  const editPassportField = (id, k, v) => setPassportEdits(pe => ({ ...pe, [id]: { number: passports.find(p=>p.id===id)?.number || '', expiry: passports.find(p=>p.id===id)?.expiry || '', ...pe[id], [k]: v } }));
  const savePassportEdit = async (id) => {
    const edit = passportEdits[id];
    if (!edit) return;
    setPassportRowSaving(s => ({ ...s, [id]: true }));
    try {
      const res = await api.freelancers.updateMyPassport(id, edit);
      setPassports(p => p.map(x => x.id === id ? res.passport : x));
      setPassportEdits(pe => { const n = { ...pe }; delete n[id]; return n; });
    } catch (e) { setError(e.message || 'Failed to update passport'); }
    finally { setPassportRowSaving(s => ({ ...s, [id]: false })); }
  };
  const removePassport = async (id) => {
    try { await api.freelancers.deleteMyPassport(id); setPassports(p => p.filter(x => x.id !== id)); }
    catch (e) { setError(e.message || 'Failed to remove passport'); }
  };

  // ── Documents: passport / photo / cv / other (persisted immediately) ─────
  const [docs, setDocs] = useState({
    passport: freelancer?.passportFiles || [],
    photo:    freelancer?.photoFiles    || [],
    cv:       freelancer?.cvFiles       || [],
    other:    (freelancer?.otherDocs || []).flatMap(d => d.files || []),
  });
  const [uploading, setUploading] = useState({});

  const uploadDocs = async (category, fileList, extra = {}) => {
    setUploading(u => ({ ...u, [category]: true }));
    setError('');
    try {
      const res = await api.freelancers.uploadMyDocuments(category, fileList, extra);
      setDocs(d => ({ ...d, [category]: [...(d[category] || []), ...res.documents] }));
    } catch (e) { setError(e.message || 'Upload failed'); }
    finally { setUploading(u => ({ ...u, [category]: false })); }
  };
  const removeDoc = async (category, docId) => {
    try {
      await api.freelancers.deleteMyDocument(docId);
      setDocs(d => ({ ...d, [category]: d[category].filter(x => x.id !== docId) }));
    } catch (e) { setError(e.message || 'Failed to remove file'); }
  };

  const [otherTitle, setOtherTitle] = useState('');

  // ── Languages (client state → saved in final PATCH /me) ──────────────────
  const [languages, setLanguages] = useState(freelancer?.languagesDetail || []);
  const [newLang, setNewLang] = useState({ language: '', level: 'Fluent' });
  const addLanguage = () => {
    if (!newLang.language) return;
    setLanguages(l => [...l, newLang]);
    setNewLang({ language: '', level: 'Fluent' });
  };
  const removeLanguage = (i) => setLanguages(l => l.filter((_, idx) => idx !== i));

  // ── Departments + per-department level (мультивибір — можна декілька рівнів одразу) ─
  const [departments, setDepartments] = useState(freelancer?.departments || []);
  const [deptLevels,  setDeptLevels]  = useState(() => {
    const raw = freelancer?.departmentLevels || {};
    // Нормалізація: старі профілі могли мати одиночний рядок замість масиву.
    const normalized = {};
    Object.keys(raw).forEach(code => { normalized[code] = Array.isArray(raw[code]) ? raw[code] : [raw[code]].filter(Boolean); });
    return normalized;
  });
  const toggleDept = (code) => {
    setDepartments(d => {
      if (d.includes(code)) {
        setDeptLevels(dl => { const n = { ...dl }; delete n[code]; return n; });
        return d.filter(x => x !== code);
      }
      return [...d, code];
    });
  };
  const toggleDeptLevel = (code, level) => setDeptLevels(dl => {
    const current = dl[code] || [];
    const next = current.includes(level) ? current.filter(l => l !== level) : [...current, level];
    return { ...dl, [code]: next };
  });

  // ── Additional skills (список, що поповнюється) ───────────────────────────
  const [skills, setSkills] = useState(freelancer?.skills || []);
  const [newSkill, setNewSkill] = useState('');
  const addSkill = () => {
    if (!newSkill.trim()) return;
    setSkills(s => [...s, newSkill.trim()]);
    setNewSkill('');
  };
  const removeSkill = (i) => setSkills(s => s.filter((_, idx) => idx !== i));

  // ── Validation per step ───────────────────────────────────────────────────
  const validateStep = (i) => {
    if (i === 0) {
      if (!form.firstName || !form.lastName || !form.dob || !form.nationality || !form.gender || !form.maritalStatus || !form.birthCountry || !form.birthCity) {
        return 'Please fill in all required fields: first name, surname, date of birth, nationality, gender, marital status, country/city of birth.';
      }
    }
    if (i === 1) {
      if (!form.phone || !form.whatsapp || !form.address.country || !form.address.city || !form.address.street || !form.address.zip) {
        return 'Please fill in phone, WhatsApp and full address (country, city, full address, ZIP).';
      }
      if (!form.emergencyContact.phone || !form.emergencyContact.email) {
        return 'Please fill in emergency contact (phone and email).';
      }
    }
    if (i === 2) {
      if (!form.passportNumber || !form.passportExpiry) return 'Passport number and expiry are required.';
      if (!docs.passport.length) return 'Please upload a copy of your passport.';
      if (!docs.photo.length)    return 'Please upload a personal photo (white background).';
    }
    if (i === 3) {
      if (!bankAccounts.length) return 'Please add at least one bank account.';
      const incomplete = bankAccounts.some(a => !a.bankName || !a.accountNumber || !a.swift || !a.accountHolderName || !a.currency);
      if (incomplete) return 'Please fill in all fields for every bank account: bank name, IBAN/account number, SWIFT, account holder name, currency.';
    }
    if (i === 4) {
      const tp = form.travelPortDetail;
      if (!tp.country || !tp.city || !tp.code) return 'Please fill in the port of travel (country, city, airport code).';
      if (!languages.length) return 'Please add at least one language.';
    }
    if (i === 5) {
      if (!departments.length) return 'Please select at least one department.';
      const missingLevel = departments.some(d => !(deptLevels[d] || []).length);
      if (missingLevel) return 'Please select at least one level for each selected department.';
      if (!docs.cv.length) return 'Please upload your CV.';
    }
    return null;
  };

  // Фрілансер міг ввести номер/дату другого паспорта в полі "Have another
  // passport?", але не натиснути "+ Add" — це лишалось локальним чернетковим
  // станом і губилось, коли він переходив далі (адмін бачив картку без
  // другого паспорта). Тепер автоматично зберігаємо цю чернетку на будь-якому
  // переході зі Step 3, а не лише при явному кліку на "+ Add".
  const maybeAddPendingPassport = async () => {
    if (step === 2 && newPassport.number && newPassport.number.trim()) {
      await addPassport();
    }
  };

  const goNext = async () => {
    const err = validateStep(step);
    if (err) { setError(err); return; }
    await maybeAddPendingPassport();
    setError('');
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = async () => {
    await maybeAddPendingPassport();
    setError('');
    setStep(s => Math.max(s - 1, 0));
  };

  const [savedMsg, setSavedMsg] = useState('');

  const handleSubmit = async () => {
    const err = validateStep(step);
    if (err) { setError(err); return; }
    await maybeAddPendingPassport();
    setError('');
    setSaving(true);
    try {
      const res = await api.freelancers.updateMe({
        ...form,
        languagesDetail: languages,
        departments,
        departmentLevels: deptLevels,
        skills,
        bankAccounts,
      });
      onComplete(res.freelancer);
      if (mode === 'edit') {
        setSavedMsg('Saved ✓');
        setTimeout(() => setSavedMsg(''), 3000);
      }
    } catch (e) {
      setError(e.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif', display: 'flex', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: '620px' }}>
        {mode === 'edit' && onBack && (
          <button type="button" onClick={onBack}
            style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', borderRadius: '6px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer', marginBottom: '16px' }}>
            ← Back to Profile
          </button>
        )}

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h1 style={{ color: 'var(--text-primary)', fontSize: '22px', margin: '0 0 8px' }}>
            {mode === 'edit' ? 'My Profile & Documents' : 'Welcome to Scull Crew 👋'}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>
            {mode === 'edit'
              ? 'View and update your information and documents at any time.'
              : 'Please complete your profile — required for project assignments and documents.'}
          </p>
        </div>

        {/* Step indicator — у режимі edit можна клікати й вільно перестрибувати між розділами */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {STEPS.map((s, i) => (
            <div key={s}
              onClick={mode === 'edit' ? async () => { await maybeAddPendingPassport(); setError(''); setStep(i); } : undefined}
              style={{
                padding: '5px 10px', borderRadius: '999px', fontSize: '11px',
                background: i === step ? RED : i < step ? 'var(--success-bg)' : 'var(--bg-card)',
                color: i === step ? 'var(--text-primary)' : i < step ? 'var(--success-text)' : 'var(--text-faint)',
                border: `1px solid ${i === step ? RED : 'var(--border)'}`,
                cursor: mode === 'edit' ? 'pointer' : 'default',
              }}>
              {i + 1}. {s}
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginBottom: '14px' }}>
          <button type="button" onClick={() => setShowGuide(g => !g)}
            style={{
              background: showGuide ? RED : 'rgba(230,57,70,0.12)',
              border: `1.5px solid ${RED}`,
              color: showGuide ? 'var(--text-primary)' : RED,
              borderRadius: '999px', padding: '10px 24px', fontSize: '13.5px', fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 0 0 4px rgba(230,57,70,0.08)',
            }}>
            📖 {showGuide ? 'Hide guide for this step' : 'Show guide for this step'}
          </button>
        </div>

        {showGuide && GUIDE_STEPS[step] && (
          <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 24px', marginBottom: '16px' }}>
            <h3 style={{ color: RED, fontSize: '13px', margin: '0 0 10px' }}>Guide — Step {step + 1}: {GUIDE_STEPS[step].title}</h3>
            {GUIDE_STEPS[step].intro && (
              <p style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6, margin: '0 0 10px' }}>{GUIDE_STEPS[step].intro}</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {GUIDE_STEPS[step].fields.map(f => (
                <div key={f.label} style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                  <span style={{
                    fontSize: '9px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '4px', flexShrink: 0,
                    color: f.required ? RED : 'var(--text-faint)',
                    background: f.required ? 'rgba(230,57,70,0.12)' : 'var(--bg-card)',
                    border: `1px solid ${f.required ? 'rgba(230,57,70,0.4)' : 'var(--border)'}`,
                  }}>
                    {f.required ? 'REQUIRED' : 'OPTIONAL'}
                  </span>
                  <div>
                    <span style={{ color: 'var(--text-body)', fontSize: '12px', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: 'var(--text-faint)', fontSize: '12px' }}> — {f.note}</span>
                  </div>
                </div>
              ))}
            </div>
            {GUIDE_STEPS[step].visualExamples && <PhotoAndPassportExamples />}
            {GUIDE_STEPS[step].warning && (
              <p style={{ color: 'var(--warning-text)', fontSize: '11px', marginTop: '12px', marginBottom: 0 }}>{GUIDE_STEPS[step].warning}</p>
            )}
          </div>
        )}

        <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px' }}>
          {error && (
            <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '18px' }}>
              {error}
            </div>
          )}

          {/* ── Step 0: Personal ─────────────────────────────────────────── */}
          {step === 0 && (
            <>
              <Row2>
                <Field label="Surname" required>
                  <input style={inputStyle} value={form.lastName} onChange={e => set('lastName', e.target.value)} />
                </Field>
                <Field label="First name" required>
                  <input style={inputStyle} value={form.firstName} onChange={e => set('firstName', e.target.value)} />
                </Field>
              </Row2>
              <Field label="Middle name">
                <input style={inputStyle} value={form.middleName} onChange={e => set('middleName', e.target.value)} />
              </Field>
              <Row2>
                <Field label="Date of birth" required>
                  <input type="date" style={inputStyle} value={form.dob} onChange={e => set('dob', e.target.value)} />
                </Field>
                <Field label="Nationality" required>
                  <input style={inputStyle} value={form.nationality} onChange={e => set('nationality', e.target.value)} />
                </Field>
              </Row2>
              <Row2>
                <Field label="Gender" required>
                  <select style={inputStyle} value={form.gender} onChange={e => set('gender', e.target.value)}>
                    <option value="">Select…</option>
                    {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Marital status" required>
                  <select style={inputStyle} value={form.maritalStatus} onChange={e => set('maritalStatus', e.target.value)}>
                    <option value="">Select…</option>
                    {MARITAL.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
              </Row2>
              <Row2>
                <Field label="Country of birth" required>
                  <input style={inputStyle} value={form.birthCountry} onChange={e => set('birthCountry', e.target.value)} />
                </Field>
                <Field label="City of birth" required>
                  <input style={inputStyle} value={form.birthCity} onChange={e => set('birthCity', e.target.value)} />
                </Field>
              </Row2>
            </>
          )}

          {/* ── Step 1: Contact & address ────────────────────────────────── */}
          {step === 1 && (
            <>
              <Row2>
                <Field label="Phone" required>
                  <input style={inputStyle} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+380..." />
                </Field>
                <Field label="WhatsApp" required>
                  <input style={inputStyle} value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="+380..." />
                </Field>
              </Row2>
              <Field label="Additional phone">
                <input style={inputStyle} value={form.additionalPhone} onChange={e => set('additionalPhone', e.target.value)} />
              </Field>
              <Field label="Email">
                <input style={inputDisabledStyle} value={freelancer?.email || ''} disabled />
              </Field>
              <div style={{ borderTop: '1px solid var(--bg-subtle)', margin: '8px 0 16px', paddingTop: '16px' }}>
                <p style={{ color: 'var(--text-faint)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>Address</p>
                <Row2>
                  <Field label="Country" required>
                    <input style={inputStyle} value={form.address.country} onChange={e => setNested('address', 'country', e.target.value)} />
                  </Field>
                  <Field label="City" required>
                    <input style={inputStyle} value={form.address.city} onChange={e => setNested('address', 'city', e.target.value)} />
                  </Field>
                </Row2>
                <Row2>
                  <Field label="Full address" required>
                    <input style={inputStyle} value={form.address.street} onChange={e => setNested('address', 'street', e.target.value)} />
                  </Field>
                  <Field label="ZIP" required>
                    <input style={inputStyle} value={form.address.zip} onChange={e => setNested('address', 'zip', e.target.value)} />
                  </Field>
                </Row2>
              </div>
              <div style={{ borderTop: '1px solid var(--bg-subtle)', margin: '8px 0 16px', paddingTop: '16px' }}>
                <p style={{ color: 'var(--text-faint)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>Emergency contact</p>
                <Row2>
                  <Field label="Phone number" required>
                    <input style={inputStyle} value={form.emergencyContact.phone} onChange={e => setNested('emergencyContact', 'phone', e.target.value)} placeholder="+380..." />
                  </Field>
                  <Field label="Email" required>
                    <input type="email" style={inputStyle} value={form.emergencyContact.email} onChange={e => setNested('emergencyContact', 'email', e.target.value)} />
                  </Field>
                </Row2>
              </div>
            </>
          )}

          {/* ── Step 2: Passport & ID ────────────────────────────────────── */}
          {step === 2 && (
            <>
              <Row2>
                <Field label="Passport number" required>
                  <input style={inputStyle} value={form.passportNumber} onChange={e => set('passportNumber', e.target.value)} />
                </Field>
                <Field label="Passport expiry" required>
                  <input type="date" style={inputStyle} value={form.passportExpiry} onChange={e => set('passportExpiry', e.target.value)} />
                </Field>
              </Row2>

              {passports.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  {passports.map(p => {
                    const edit = passportEdits[p.id];
                    const dirty = Boolean(edit);
                    return (
                      <div key={p.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px', marginBottom: '6px' }}>
                        <input style={{ ...inputStyle, flex: 1 }} value={edit?.number ?? p.number} onChange={e => editPassportField(p.id, 'number', e.target.value)} placeholder="Number" />
                        <input type="date" style={{ ...inputStyle, flex: 1 }} value={edit?.expiry ?? p.expiry ?? ''} onChange={e => editPassportField(p.id, 'expiry', e.target.value)} />
                        {dirty && (
                          <button type="button" disabled={passportRowSaving[p.id]} onClick={() => savePassportEdit(p.id)}
                            style={{ background: RED, color: 'var(--text-primary)', border: 'none', borderRadius: '6px', padding: '0 12px', height: '38px', cursor: 'pointer', flexShrink: 0, fontSize: '12px' }}>
                            {passportRowSaving[p.id] ? '…' : 'Save'}
                          </button>
                        )}
                        <button type="button" onClick={() => removePassport(p.id)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', flexShrink: 0 }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              )}
              <Field label="Have another passport?" hint="Optional — add if you hold more than one passport.">
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Number" value={newPassport.number} onChange={e => setNewPassport(p => ({ ...p, number: e.target.value }))} />
                  <input type="date" style={{ ...inputStyle, flex: 1 }} value={newPassport.expiry} onChange={e => setNewPassport(p => ({ ...p, expiry: e.target.value }))} />
                  <button type="button" disabled={passportSaving || !newPassport.number} onClick={addPassport}
                    style={{ background: 'var(--border)', color: 'var(--text-primary)', border: 'none', borderRadius: '6px', padding: '0 16px', cursor: 'pointer', flexShrink: 0 }}>
                    + Add
                  </button>
                </div>
                {newPassport.number && (
                  <p style={{ color: 'var(--warning-text)', fontSize: '11px', margin: '6px 0 0' }}>
                    It will be saved automatically when you continue, even without clicking "+ Add".
                  </p>
                )}
              </Field>

              <div style={{ borderTop: '1px solid var(--bg-subtle)', margin: '16px 0', paddingTop: '16px' }}>
                <Field label="Passport copy (scan/photo)" required>
                  <UploadButton label="Upload passport copy" accept="image/*,.pdf" uploading={uploading.passport}
                    onFiles={files => uploadDocs('passport', files)} />
                  {docs.passport.map(d => <DocChip key={d.id} doc={d} onRemove={() => removeDoc('passport', d.id)} />)}
                </Field>

                <Field label="Personal photo" required hint="On a white background, like an ID/document photo.">
                  <UploadButton label="Upload photo" accept="image/*" uploading={uploading.photo}
                    onFiles={files => uploadDocs('photo', files)} />
                  {docs.photo.map(d => <DocChip key={d.id} doc={d} onRemove={() => removeDoc('photo', d.id)} />)}
                </Field>
              </div>
            </>
          )}

          {/* ── Step 3: Financial ────────────────────────────────────────── */}
          {step === 3 && (
            <>
              {bankAccounts.map((acc, i) => {
                const isCustomCurrency = !CURRENCIES.includes(acc.currency);
                return (
                  <div key={i} style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '8px', padding: '16px', marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <p style={{ color: 'var(--text-faint)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Bank {i + 1}</p>
                      {bankAccounts.length > 1 && (
                        <button type="button" onClick={() => removeBankAccount(i)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '13px' }}>✕ Remove</button>
                      )}
                    </div>
                    <Field label="Bank name" required>
                      <input style={inputStyle} value={acc.bankName} onChange={e => setBankField(i, 'bankName', e.target.value)} />
                    </Field>
                    <Field label="IBAN / Account number" required>
                      <input style={inputStyle} value={acc.accountNumber} onChange={e => setBankField(i, 'accountNumber', e.target.value)} />
                    </Field>
                    <Row2>
                      <Field label="SWIFT" required>
                        <input style={inputStyle} value={acc.swift} onChange={e => setBankField(i, 'swift', e.target.value)} />
                      </Field>
                      <Field label="Account holder full name" required>
                        <input style={inputStyle} value={acc.accountHolderName} onChange={e => setBankField(i, 'accountHolderName', e.target.value)} />
                      </Field>
                    </Row2>
                    <Field label="Currency" required>
                      <select
                        style={inputStyle}
                        value={isCustomCurrency ? '__custom__' : acc.currency}
                        onChange={e => setBankField(i, 'currency', e.target.value === '__custom__' ? '' : e.target.value)}
                      >
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        <option value="__custom__">Other…</option>
                      </select>
                      {isCustomCurrency && (
                        <input
                          style={{ ...inputStyle, marginTop: '8px' }}
                          placeholder="Currency code (e.g. GBP)"
                          maxLength={10}
                          value={acc.currency}
                          onChange={e => setBankField(i, 'currency', e.target.value.toUpperCase().slice(0, 10))}
                        />
                      )}
                    </Field>
                  </div>
                );
              })}
              <button type="button" onClick={addBankAccount}
                style={{ background: 'var(--bg-card)', border: '1px dashed var(--text-disabled)', color: 'var(--text-secondary)', borderRadius: '6px', padding: '9px 16px', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>
                + Add another bank
              </button>
              <p style={{ color: 'var(--text-dim)', fontSize: '12px', marginTop: '8px' }}>
                ⚠️ Keeping this information accurate and up to date is your responsibility as a freelancer.
              </p>
            </>
          )}

          {/* ── Step 4: Travel & languages ───────────────────────────────── */}
          {step === 4 && (
            <>
              <p style={{ color: 'var(--text-faint)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>Port of travel</p>
              <Row2>
                <Field label="Country" required>
                  <input style={inputStyle} value={form.travelPortDetail.country} onChange={e => setNested('travelPortDetail', 'country', e.target.value)} />
                </Field>
                <Field label="City" required>
                  <input style={inputStyle} value={form.travelPortDetail.city} onChange={e => setNested('travelPortDetail', 'city', e.target.value)} />
                </Field>
              </Row2>
              <Field label="Airport code" required hint="e.g. KBP">
                <input style={inputStyle} maxLength={10} value={form.travelPortDetail.code} onChange={e => setNested('travelPortDetail', 'code', e.target.value.toUpperCase().slice(0, 10))} />
              </Field>

              <div style={{ borderTop: '1px solid var(--bg-subtle)', margin: '16px 0', paddingTop: '16px' }}>
                <p style={{ color: 'var(--text-faint)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>Languages</p>
                {languages.map((l, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 12px', marginBottom: '6px', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-body)' }}>{l.language} <span style={{ color: 'var(--text-faint)' }}>· {l.level}</span></span>
                    <button type="button" onClick={() => removeLanguage(i)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer' }}>✕</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Language" value={newLang.language} onChange={e => setNewLang(l => ({ ...l, language: e.target.value }))} />
                  <select style={{ ...inputStyle, width: '160px' }} value={newLang.level} onChange={e => setNewLang(l => ({ ...l, level: e.target.value }))}>
                    {LANG_LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                  </select>
                  <button type="button" disabled={!newLang.language} onClick={addLanguage}
                    style={{ background: 'var(--border)', color: 'var(--text-primary)', border: 'none', borderRadius: '6px', padding: '0 16px', cursor: 'pointer', flexShrink: 0 }}>
                    + Add
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── Step 5: Professional ─────────────────────────────────────── */}
          {step === 5 && (
            <>
              <Field label="Departments" required hint="Select all that apply.">
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {DEPARTMENTS.map(d => (
                    <button key={d} type="button" onClick={() => toggleDept(d)}
                      style={{
                        background: departments.includes(d) ? RED : 'var(--bg-card)',
                        color: departments.includes(d) ? 'var(--text-primary)' : 'var(--text-secondary)',
                        border: `1px solid ${departments.includes(d) ? RED : 'var(--border-strong)'}`,
                        borderRadius: '6px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer',
                      }}>
                      {d} — {DEPT_FULL[d]}
                    </button>
                  ))}
                </div>
              </Field>

              {departments.length > 0 && (
                <Field label="Level per department" required hint="You can select more than one level for the same department.">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {departments.map(d => (
                      <div key={d}>
                        <span style={{ color: 'var(--text-body)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>{d} — {DEPT_FULL[d]}</span>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {DEPT_LEVELS.map(lv => {
                            const active = (deptLevels[d] || []).includes(lv);
                            return (
                              <button key={lv} type="button" onClick={() => toggleDeptLevel(d, lv)}
                                style={{
                                  background: active ? RED : 'var(--bg-card)',
                                  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                                  border: `1px solid ${active ? RED : 'var(--border-strong)'}`,
                                  borderRadius: '6px', padding: '7px 12px', fontSize: '12px', cursor: 'pointer',
                                }}>
                                {active ? '✓ ' : ''}{lv}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </Field>
              )}

              <Field
                label="Additional skills"
                hint="Optional. Here you can mention any skills which are related to your position. You can mention control desks, any equipment or licenses."
              >
                {skills.map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 12px', marginBottom: '6px', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-body)' }}>{s}</span>
                    <button type="button" onClick={() => removeSkill(i)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer' }}>✕</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder="e.g. GrandMA3, Avolites, Vectorworks…"
                    value={newSkill}
                    onChange={e => setNewSkill(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
                  />
                  <button type="button" disabled={!newSkill.trim()} onClick={addSkill}
                    style={{ background: 'var(--border)', color: 'var(--text-primary)', border: 'none', borderRadius: '6px', padding: '0 16px', cursor: 'pointer', flexShrink: 0 }}>
                    + Add
                  </button>
                </div>
              </Field>

              <Field label="Documents (certificates, permits, licenses)" hint="Optional.">
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Title (e.g. Rigging certificate)" value={otherTitle} onChange={e => setOtherTitle(e.target.value)} />
                  <UploadButton label="Upload" accept="image/*,.pdf,.doc,.docx" multiple={false} uploading={uploading.other}
                    onFiles={files => { uploadDocs('other', files, { title: otherTitle || undefined }); setOtherTitle(''); }} />
                </div>
                {docs.other.map(d => <DocChip key={d.id} doc={d} onRemove={() => removeDoc('other', d.id)} />)}
              </Field>

              <Field label="CV" required>
                <UploadButton label="Upload CV" accept=".pdf,.doc,.docx" multiple={false} uploading={uploading.cv}
                  onFiles={files => uploadDocs('cv', files)} />
                {docs.cv.map(d => <DocChip key={d.id} doc={d} onRemove={() => removeDoc('cv', d.id)} />)}
              </Field>

              <Field label="Comment" hint="Optional — anything else you'd like us to know.">
                <textarea
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }}
                  value={form.freelancerComment}
                  onChange={e => set('freelancerComment', e.target.value)}
                />
              </Field>
            </>
          )}

          {/* ── Navigation ────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', gap: '12px' }}>
            <button
              type="button" onClick={goBack} disabled={step === 0}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: step === 0 ? 'var(--text-disabled)' : 'var(--text-secondary)', borderRadius: '8px', padding: '12px 20px', fontSize: '14px', cursor: step === 0 ? 'not-allowed' : 'pointer' }}
            >
              ← Back
            </button>
            {savedMsg && <span style={{ color: 'var(--success-text)', fontSize: '13px', alignSelf: 'center' }}>{savedMsg}</span>}
            <div style={{ display: 'flex', gap: '10px' }}>
              {/* У режимі edit — Save доступний з будь-якого кроку, а не лише останнього. */}
              {mode === 'edit' && (
                <button
                  type="button" onClick={handleSubmit} disabled={saving}
                  style={{ background: saving ? 'var(--text-dim)' : RED, color: 'var(--text-primary)', border: 'none', borderRadius: '8px', padding: '12px 28px', fontSize: '14px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button
                  type="button" onClick={goNext}
                  style={{ background: mode === 'edit' ? 'transparent' : RED, border: mode === 'edit' ? '1px solid var(--border-strong)' : 'none', color: mode === 'edit' ? 'var(--text-secondary)' : 'var(--text-primary)', borderRadius: '8px', padding: '12px 28px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Next →
                </button>
              ) : mode !== 'edit' && (
                <button
                  type="button" onClick={handleSubmit} disabled={saving}
                  style={{ background: saving ? 'var(--text-dim)' : RED, color: 'var(--text-primary)', border: 'none', borderRadius: '8px', padding: '12px 28px', fontSize: '14px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
                >
                  {saving ? 'Saving…' : 'Submit & continue'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
